/**
 * Mobile resilience utilities for iOS (Apple) and Android devices.
 * Handles Bluetooth capability detection, background execution preservation, and screen wake lock.
 */

export function isAppleDevice() {
  if (typeof window === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function isWebBluetoothSupported() {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator && !!navigator.bluetooth
}

export function getBluetoothErrorMessage() {
  if (!isWebBluetoothSupported()) {
    if (isAppleDevice()) {
      return 'En iPhone/iPad, Apple no incluye Bluetooth en Safari. Para conectar tu sensor Polar en iOS, abrí esta web con la app gratuita Bluefy (disponible en App Store) o utilizá el modo Bridge/Compartir.'
    }
    return 'Tu navegador actual no soporta Web Bluetooth. Te recomendamos usar Google Chrome, Microsoft Edge o Brave en Android o PC.'
  }
  return null
}

/**
 * Keeps Web Bluetooth GATT communication active when the mobile screen is turned off
 * or when the user temporarily switches tabs by playing an inaudible audio carrier.
 */
class MobileBackgroundKeeper {
  constructor() {
    this.ctx = null
    this.osc = null
    this.gain = null
    this.active = false
  }

  start() {
    if (this.active) return
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return

      if (!this.ctx || this.ctx.state === 'closed') {
        this.ctx = new AudioCtx()
      }

      if (this.ctx.state === 'suspended') {
        this.ctx.resume()
      }

      if (!this.osc) {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()
        // Virtually inaudible silence to keep audio subsystem awake
        gain.gain.value = 0.00001
        osc.frequency.value = 40
        osc.connect(gain)
        gain.connect(this.ctx.destination)
        osc.start()
        this.osc = osc
        this.gain = gain
      }
      this.active = true
    } catch (_) {}
  }

  stop() {
    if (!this.active) return
    try {
      if (this.osc) {
        this.osc.stop()
        this.osc.disconnect()
        this.osc = null
      }
      if (this.ctx && this.ctx.state !== 'closed') {
        this.ctx.suspend()
      }
      this.active = false
    } catch (_) {}
  }
}

export const backgroundKeeper = new MobileBackgroundKeeper()
